import {
  MANAGE_TIN_COLLECTION_SUCCESS,
  MANAGE_TIN_COLLECTION_TITLE,
  TIN_COLLECTION_OPTIONS
} from '@bo-constants/member/memberCopy';
import { MemberConfirmationModal } from '@bo-components/members/modal';
import { PrizepicksSwitch } from '@library/components/switches';
import { RadioGroup } from '@library/components/inputs';
import { classNames } from '@library/utils';
import { isConfirmationErrorMessage } from '@bo-utils/members/memberCommon';
import { useMemberTinStatus } from '@bo-hooks/members';
import { useMemo } from 'react';

export interface MemberManageTinCollectionModalProps {
  memberId: string;
  needsTid?: boolean;
  onClose: () => void;
  tidValidation?: number;
}

export const MemberManageTinCollectionModal = ({
  memberId,
  needsTid = undefined,
  tidValidation = 0,
  onClose
}: MemberManageTinCollectionModalProps) => {
  const {
    currentTidStatus,
    errorMessage,
    handlePromptSSNChange,
    handleTidValidationChange,
    handleSubmit,
    isSubmitLoading,
    isSubmitSuccess,
    promptSSN,
    showConfirmationMessage,
    tidValidationValue
  } = useMemberTinStatus({
    memberId,
    needsTid,
    tidValidation
  });

  const UpdateTinValidation = useMemo(() => {
    return (
      <div className="text-soClean-180 flex flex-col text-sm">
        <p className="text-soClean-100 mb-6">{currentTidStatus}</p>
        <p className="mb-2">TID validation</p>
        <RadioGroup
          className="mb-6 flex flex-col items-start gap-4"
          containerClassName="relative flex w-full items-center [&>*]:cursor-pointer"
          dataTestId="tin-collection"
          disabled={isSubmitLoading}
          id="tinCollection"
          inputClassName="absolute left-4 peer"
          labelClassName="border-soFresh-140 peer-checked:border-soClean-100 flex w-full flex-col gap-1 rounded-lg border p-4 pl-12 text-sm m-0"
          onValueChange={handleTidValidationChange}
          radioOptions={TIN_COLLECTION_OPTIONS}
          selectedOption={String(tidValidationValue)}
        />
        <div className="flex">
          <p>Prompt member for SSN</p>
          <PrizepicksSwitch
            active={promptSSN}
            className="ml-auto"
            disabled={isSubmitLoading}
            handleChange={handlePromptSSNChange}
          />
        </div>
      </div>
    );
  }, [
    currentTidStatus,
    handlePromptSSNChange,
    handleTidValidationChange,
    isSubmitLoading,
    promptSSN,
    tidValidationValue
  ]);

  const ConfirmationMessage = useMemo(() => {
    const confirmationMessage = isSubmitSuccess
      ? MANAGE_TIN_COLLECTION_SUCCESS
      : errorMessage;
    const isErrorMessage = isConfirmationErrorMessage(confirmationMessage);

    return (
      <p
        className={classNames(
          'text-sm',
          isErrorMessage ? 'text-roses' : 'text-soClean-180'
        )}
      >
        {confirmationMessage}
      </p>
    );
  }, [errorMessage, isSubmitSuccess]);

  return (
    <MemberConfirmationModal
      cancelCopy="Cancel"
      childrenClassName="justify-evenly min-h-32"
      className="w-[25rem]"
      confirmationCopy="Save"
      handleConfirm={handleSubmit}
      isOpen
      label={MANAGE_TIN_COLLECTION_TITLE}
      onCancelModal={onClose}
      showConfirmationMessage={showConfirmationMessage}
    >
      {!showConfirmationMessage ? UpdateTinValidation : ConfirmationMessage}
    </MemberConfirmationModal>
  );
};
